import { create } from 'zustand';

/**
 * Navigation views
 */
export type NavView = 'warmup' | 'lanes' | 'challenges' | 'leaderboard' | 'profile' | 'pro' | 'team' | 'report' | 'team-leaderboard' | 'analytics' | 'team-admin' | 'tournaments' | 'settings';

/**
 * Modal types
 */
export type ModalType = 
  | 'none'
  | 'settings'
  | 'level-complete'
  | 'run-complete'
  | 'reveal'
  | 'confirm-quit';

/**
 * Toast notification
 */
export interface Toast {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  message: string;
  duration?: number;
}

/**
 * UI store state
 */
interface UIState {
  // Navigation
  currentView: NavView;
  previousView: NavView | null;
  
  // Panels
  isLeftNavExpanded: boolean;
  isRightDrawerOpen: boolean;
  rightDrawerTab: 'coach' | 'attempts' | 'settings';
  
  // Modals
  activeModal: ModalType;
  modalData: Record<string, unknown>;
  
  // Toasts
  toasts: Toast[];
  
  // Game HUD
  isHUDVisible: boolean;
  showFPSCounter: boolean;
  
  // Actions
  navigate: (view: NavView) => void;
  goBack: () => void;
  toggleLeftNav: () => void;
  openRightDrawer: (tab?: 'coach' | 'attempts' | 'settings') => void;
  closeRightDrawer: () => void;
  setRightDrawerTab: (tab: 'coach' | 'attempts' | 'settings') => void;
  openModal: (modal: ModalType, data?: Record<string, unknown>) => void;
  closeModal: () => void;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  setHUDVisible: (visible: boolean) => void;
  toggleFPSCounter: () => void;
}

/**
 * Generate unique toast ID
 */
const generateToastId = () =>
  `toast_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

export const useUIStore = create<UIState>()((set, get) => ({
  // Initial state
  currentView: 'warmup',
  previousView: null,
  isLeftNavExpanded: true,
  isRightDrawerOpen: false,
  rightDrawerTab: 'coach',
  activeModal: 'none',
  modalData: {},
  toasts: [],
  isHUDVisible: true,
  showFPSCounter: false,

  // Actions
  navigate: (view) =>
    set((state) => ({
      previousView: state.currentView,
      currentView: view,
    })),

  goBack: () => {
    const { previousView } = get();
    if (previousView) {
      set((state) => ({
        currentView: previousView,
        previousView: state.currentView,
      }));
    }
  },

  toggleLeftNav: () =>
    set((state) => ({
      isLeftNavExpanded: !state.isLeftNavExpanded,
    })),

  openRightDrawer: (tab) =>
    set({
      isRightDrawerOpen: true,
      rightDrawerTab: tab ?? 'coach',
    }),

  closeRightDrawer: () => set({ isRightDrawerOpen: false }),

  setRightDrawerTab: (tab) => set({ rightDrawerTab: tab }),

  openModal: (modal, data = {}) =>
    set({
      activeModal: modal,
      modalData: data,
    }),

  closeModal: () =>
    set({
      activeModal: 'none',
      modalData: {},
    }),

  addToast: (toast) => {
    const id = generateToastId();
    const newToast: Toast = { ...toast, id };
    
    set((state) => ({
      toasts: [...state.toasts, newToast],
    }));

    // Auto-remove after duration
    const duration = toast.duration ?? 3000;
    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }
  },

  removeToast: (id) =>
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    })),

  setHUDVisible: (visible) => set({ isHUDVisible: visible }),

  toggleFPSCounter: () =>
    set((state) => ({
      showFPSCounter: !state.showFPSCounter,
    })),
}));

/**
 * Selector: Check if any modal is open
 */
export const selectIsModalOpen = (state: UIState) => state.activeModal !== 'none';

/**
 * Selector: Check if in game view
 */
export const selectIsInGame = (state: UIState) =>
  state.currentView === 'warmup' || state.currentView === 'lanes' || state.currentView === 'challenges';
